import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects"

import {
  GET_CAMPAIGN_ANALYSIS,
  GET_PING_DETAILS,
  EXPORT_CAMPAIGN_ANALYSIS,
} from "./actionTypes"

import {
  getCampaignAnalysisSuccess,
  getCampaignAnalysisError,
  getPingDetailsSuccess,
  getPingDetailsError,
  exportCampaignAnalysisSuccess,
  exportCampaignAnalysisError,
} from "./actions"

import { genericGetRequest_2, genericPostRequest } from "helpers/sf_backend_helper.js"
import { bsh_getAdminUserDetails, bsh_getClientUserDetails, bsh_isAdminUserDetailsPresent } from "helpers/sf_browser_storage_helper"
import { PRODUCT_TYPE_IDS } from "helpers/sf_constants"
import { showExportEnqueuedToast } from "helpers/export_toast_helper"
import { buildCampaignAnalysisCacheKey, getCurrentBrandFromLocalStorage, showErrorToast } from "helpers/sf_utils"
import { AppConfiguration } from "config"

const getCampaignAnalysisExportEndpoint = (productTypeId) => {
  switch (Number(productTypeId)) {
    case PRODUCT_TYPE_IDS.CLICKS:
      return '/api/v1/analyze_campaign/clicks/export'
    case PRODUCT_TYPE_IDS.LEADS:
      return '/api/v1/analyze_campaign/leads/export'
    case PRODUCT_TYPE_IDS.CALLS:
      return '/api/v1/analyze_campaign/calls/export'
    default:
      throw new Error('Unsupported campaign analysis product type')
  }
}

const inFlightCampaignAnalysisRequests = new Set()

function* onGetCampaignAnalysis(action) {
  try {
    const useCache = Boolean(action.payload?.useCache)
    const queryParams = action.payload?.queryParams || {}
    const requestedPageNumber = parseInt(queryParams.page_number, 10) || 1
    const cacheKey = useCache ? buildCampaignAnalysisCacheKey(queryParams) : null
    const requestKey = `${cacheKey}:${requestedPageNumber}`
    const cachedPage = useCache
      ? yield select((state) => state.CampaignAnalysis.page_cache?.[cacheKey]?.[requestedPageNumber])
      : null

    if (useCache && cachedPage) {
      return
    }

    if (useCache && inFlightCampaignAnalysisRequests.has(requestKey)) {
      return
    }

    if (useCache) {
      inFlightCampaignAnalysisRequests.add(requestKey)
    }

    let user = JSON.parse(localStorage.getItem("authUser")).user

    const response = yield call(
      genericGetRequest_2,
      `${AppConfiguration.BACKEND_URL}/api/v1/analyze_campaign`,
      queryParams,
      {
        Authorization: user.token,
        'account-id': user.account_id,
        'brand-id': getCurrentBrandFromLocalStorage()?.id

      }
    )
    yield put(getCampaignAnalysisSuccess(response, {
      queryParams,
      appendResults: Boolean(action.payload?.appendResults),
      useCache,
    }))
  } catch (error) {
    yield put(getCampaignAnalysisError(error))
  } finally {
    if (action.payload?.useCache) {
      const queryParams = action.payload?.queryParams || {}
      const requestedPageNumber = parseInt(queryParams.page_number, 10) || 1
      const cacheKey = buildCampaignAnalysisCacheKey(queryParams)
      inFlightCampaignAnalysisRequests.delete(`${cacheKey}:${requestedPageNumber}`)
    }
  }
}

function* onGetPingDetails(action) {
  try {
    let user = bsh_getAdminUserDetails();

    const response = yield call(
      genericGetRequest_2,
      `${AppConfiguration.BACKEND_URL}/api/v1/product_ping_details`,
      action.payload,
      {
        Authorization: user.token,
      }
    )
    yield put(getPingDetailsSuccess(JSON.parse(response.data)))
  } catch (error) {
    yield put(getPingDetailsError(error))
  }
}

function* onExportCampaignAnalysis(action) {
  try {
    const { productTypeId, queryParams } = action.payload
    const endpoint = getCampaignAnalysisExportEndpoint(productTypeId)
    const user = bsh_getClientUserDetails()

    yield call(
      genericPostRequest,
      `${AppConfiguration.BACKEND_URL}${endpoint}`,
      {
        body: JSON.stringify(queryParams || {}),
        headers: {
          Authorization: user.token,
          'account-id': user.account_id,
          'brand-id': getCurrentBrandFromLocalStorage()?.id,
          'Content-Type': 'application/json',
        },
      }
    )
    yield put(exportCampaignAnalysisSuccess())
    showExportEnqueuedToast({ reportExportsAvailable: bsh_isAdminUserDetailsPresent() })
  } catch (error) {
    yield put(exportCampaignAnalysisError(error))
    showErrorToast(error, 'Failed to queue campaign analysis export.')
  }
}

function* CampaignAnalysisSaga() {
  yield takeLatest(GET_CAMPAIGN_ANALYSIS, onGetCampaignAnalysis)
  yield takeEvery(GET_PING_DETAILS, onGetPingDetails)
  yield takeEvery(EXPORT_CAMPAIGN_ANALYSIS, onExportCampaignAnalysis)
}

export default CampaignAnalysisSaga
